// src/componentes/Comisiones/ComisionistaForm.jsx
// Alta y edición de comisionistas.
// Al crear: genera automáticamente un doc espejo en proveedores.
// Al editar: actualiza el espejo si los datos cambian.

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { initializeApp, getApp, getApps } from "firebase/app";
import { doc, setDoc } from "firebase/firestore";
import {
  createComisionista,
  getComisionista,
  updateComisionista,
} from "../../services/comisionistasService";
import { ESQUEMA_OPCIONES } from "../../helpers/comisionEsquemas";
import { registrarAuditoria } from "../../helpers/auditoria";
import { db, firebaseConfig } from "../../Firebase/firebaseconfig";
import notify from "../common/notify";
import "./Comisiones.css";

// Misma instancia secundaria de Auth que GestionUsuarios — evita cerrar la sesión del admin
const getSecondaryAuth = () => {
  const appName = "user-creation";
  const secondaryApp = getApps().some((app) => app.name === appName)
    ? getApp(appName)
    : initializeApp(firebaseConfig, appName);
  return getAuth(secondaryApp);
};

const EMPTY = {
  nombre: "",
  apellidos: "",
  telefono: "",
  email: "",
  rfc: "",
  cuentaBancaria: "",
  banco: "",
  tipo: "externo",
  uid: "",                      // UID de Firebase Auth — permite acceso al portal
  tarifaPorDefecto: "3",       // string para el input, convertimos al guardar
  esquemaPorDefecto: "enganche-50-mensualidades-50",
  activo: true,
};

export default function ComisionistaForm({ edit = false }) {
  const { comisionistaId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!!edit);
  const [saving, setSaving] = useState(false);

  // Sección "Cuenta de portal" — solo al crear, no en edición
  const [crearCuenta, setCrearCuenta] = useState(false);
  const [passwordPortal, setPasswordPortal] = useState("");

  // Cargar datos si es edición
  useEffect(() => {
    if (!edit) return;
    (async () => {
      try {
        const c = await getComisionista(comisionistaId);
        if (c) {
          setForm({
            nombre: c.nombre || "",
            apellidos: c.apellidos || "",
            telefono: c.telefono || "",
            email: c.email || "",
            rfc: c.rfc || "",
            cuentaBancaria: c.cuentaBancaria || "",
            banco: c.banco || "",
            tipo: c.tipo || "externo",
            uid: c.uid || "",
            tarifaPorDefecto: String(Number((c.tarifaPorDefecto || 0.03) * 100).toFixed(2)),
            esquemaPorDefecto: c.esquemaPorDefecto || "enganche-50-mensualidades-50",
            activo: c.activo ?? true,
          });
        }
      } catch (err) {
        console.error(err);
        notify.error("No se pudieron cargar los datos.");
      } finally {
        setLoading(false);
      }
    })();
  }, [edit, comisionistaId]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const save = async () => {
    if (!form.nombre.trim()) {
      notify.error("El nombre es obligatorio.");
      return;
    }

    const tarifa = Number(form.tarifaPorDefecto) / 100;
    if (!Number.isFinite(tarifa) || tarifa <= 0 || tarifa > 1) {
      notify.error("La tarifa debe ser un porcentaje entre 0.1% y 100%.");
      return;
    }

    // Validaciones extra si se quiere crear cuenta de portal
    if (!edit && crearCuenta) {
      if (!form.email.trim()) {
        notify.error("Se necesita un email para crear la cuenta de portal.");
        return;
      }
      if (passwordPortal.length < 6) {
        notify.error("La contraseña del portal debe tener al menos 6 caracteres.");
        return;
      }
    }

    setSaving(true);
    try {
      let uid = form.uid || null;

      // ── Crear cuenta de portal si se solicitó ────────────────────────────
      if (!edit && crearCuenta) {
        const emailNorm = form.email.trim().toLowerCase();
        const secondaryAuth = getSecondaryAuth();
        const cred = await createUserWithEmailAndPassword(secondaryAuth, emailNorm, passwordPortal);
        await signOut(secondaryAuth);
        uid = cred.user.uid;

        // Doc en /usuarios con rol comisionista
        await setDoc(doc(db, "usuarios", uid), {
          nombre: `${form.nombre.trim()} ${form.apellidos.trim()}`.trim(),
          correo: emailNorm,
          rol: "comisionista",
        });

        await registrarAuditoria({
          accion: "usuarios.crear",
          entidadTipo: "usuario",
          entidadId: uid,
          despues: { nombre: form.nombre, correo: emailNorm, rol: "comisionista", origen: "ComisionistaForm" },
        });
      }

      // ── Guardar comisionista ─────────────────────────────────────────────
      const payload = { ...form, tarifaPorDefecto: tarifa, uid: uid || form.uid || null };
      if (edit) {
        await updateComisionista(comisionistaId, payload);
        notify.success("Comisionista actualizado.");
        navigate(`/app/comisionistas/${comisionistaId}`);
      } else {
        const ref = await createComisionista(payload);
        const msg = crearCuenta
          ? "Comisionista creado con cuenta de portal y proveedor espejo."
          : "Comisionista creado. Se generó el proveedor espejo.";
        notify.success(msg);
        navigate(`/app/comisionistas/${ref.id}`);
      }
    } catch (err) {
      console.error(err);
      // Traducir errores de Firebase Auth a mensajes claros
      const code = err?.code || "";
      if (code === "auth/email-already-in-use") {
        notify.error("Ya existe una cuenta de portal con ese correo. Deja el checkbox sin marcar y usa el campo 'UID de acceso' para vincular la cuenta existente.");
      } else if (code === "auth/invalid-email") {
        notify.error("El email no tiene un formato válido.");
      } else if (code === "auth/weak-password") {
        notify.error("La contraseña debe tener al menos 6 caracteres.");
      } else {
        notify.error(err.message || "No se pudo guardar.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="com-page">
        <div className="com-panel">
          <p style={{ color: "#94a3b8" }}>Cargando…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="com-page">
      <div className="com-panel">
        <div className="com-head">
          <div>
            <h2>{edit ? "Editar comisionista" : "Nuevo comisionista"}</h2>
            <p>
              {edit
                ? "Los cambios se sincronizan automáticamente al proveedor espejo."
                : "Se creará automáticamente un proveedor espejo para el flujo de CxP."}
            </p>
          </div>
        </div>

        <div className="com-form-grid">
          {/* ─── Nombre ─────────────────────────────── */}
          <label>
            Nombre *
            <input
              name="nombre"
              value={form.nombre}
              onChange={onChange}
              placeholder="Nombre(s)"
            />
          </label>

          <label>
            Apellidos
            <input
              name="apellidos"
              value={form.apellidos}
              onChange={onChange}
              placeholder="Apellido paterno y materno"
            />
          </label>

          {/* ─── Contacto ──────────────────────────── */}
          <label>
            Teléfono
            <input
              name="telefono"
              value={form.telefono}
              onChange={onChange}
              placeholder="10 dígitos"
            />
          </label>

          <label>
            Email
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              placeholder="correo@ejemplo.com"
            />
            <span className="com-form-hint">
              Debe coincidir con el correo de acceso al portal.
            </span>
          </label>

          <label className="full">
            UID de acceso al portal (opcional)
            <input
              name="uid"
              value={form.uid}
              onChange={onChange}
              placeholder="Ej: AbCdEfG12345 — ID de Firebase Auth del usuario"
              style={{ fontFamily: "monospace", fontSize: 13 }}
            />
            <span className="com-form-hint">
              Si el comisionista ya tiene una cuenta de usuario en el sistema, pega aquí su UID
              (visible en Usuarios / Roles). Permite acceso seguro al portal aunque el email no coincida.
            </span>
          </label>

          <label>
            RFC
            <input
              name="rfc"
              value={form.rfc}
              onChange={onChange}
              placeholder="XXXX000000XXX"
              style={{ textTransform: "uppercase" }}
            />
          </label>

          {/* ─── Datos bancarios ────────────────────── */}
          <label>
            Banco
            <input
              name="banco"
              value={form.banco}
              onChange={onChange}
              placeholder="BBVA, Santander…"
            />
          </label>

          <label className="full">
            Cuenta bancaria (CLABE / número de cuenta)
            <input
              name="cuentaBancaria"
              value={form.cuentaBancaria}
              onChange={onChange}
              placeholder="18 dígitos CLABE o número de cuenta"
            />
          </label>

          {/* ─── Configuración de comisión ────────── */}
          <label>
            Tarifa por defecto (%)
            <input
              name="tarifaPorDefecto"
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              value={form.tarifaPorDefecto}
              onChange={onChange}
              placeholder="3"
            />
            <span className="com-form-hint">
              Se puede ajustar al capturar cada venta. Ej: 3 → 3% de comisión.
            </span>
          </label>

          <label>
            Esquema por defecto
            <select
              name="esquemaPorDefecto"
              value={form.esquemaPorDefecto}
              onChange={onChange}
            >
              {ESQUEMA_OPCIONES.filter((o) => o.value !== "custom").map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="com-form-hint">
              Se puede cambiar al capturar cada venta.
            </span>
          </label>

          <label>
            Tipo
            <select name="tipo" value={form.tipo} onChange={onChange}>
              <option value="externo">Externo</option>
              <option value="interno">Interno (empleado)</option>
            </select>
          </label>

          {/* ─── Activo ─────────────────────────────── */}
          <label className="com-form-switch">
            <input
              type="checkbox"
              name="activo"
              checked={form.activo}
              onChange={onChange}
            />
            Activo
          </label>
        </div>

        {/* ─── Cuenta de acceso al portal (solo en alta) ─────────────── */}
        {!edit && (
          <div style={{
            marginTop: 8,
            padding: "14px 16px",
            background: crearCuenta ? "#eff6ff" : "#f8fafc",
            border: `1px solid ${crearCuenta ? "#bfdbfe" : "#e5e7eb"}`,
            borderRadius: 10,
            transition: "background 0.15s, border-color 0.15s",
          }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none", fontWeight: 700, color: "#1e40af", fontSize: 14 }}>
              <input
                type="checkbox"
                checked={crearCuenta}
                onChange={(e) => setCrearCuenta(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: "#1e40af", flexShrink: 0 }}
              />
              Crear cuenta de acceso al portal del comisionista
            </label>
            <p style={{ margin: "6px 0 0 26px", fontSize: 12, color: "#64748b" }}>
              Se creará una cuenta de Firebase Auth y un usuario con rol <b>comisionista</b> automáticamente.
            </p>

            {crearCuenta && (
              <div style={{ marginTop: 12, marginLeft: 26 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 4 }}>
                  Contraseña temporal del portal *
                </label>
                <input
                  type="password"
                  value={passwordPortal}
                  onChange={(e) => setPasswordPortal(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  style={{ width: "100%", maxWidth: 280, padding: "8px 10px", border: "1px solid #93c5fd", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                />
                <p style={{ margin: "5px 0 0", fontSize: 11, color: "#64748b" }}>
                  El comisionista puede cambiarla desde su portal. Se usará el email del campo "Email" como usuario de acceso.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="com-form-actions">
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? "Guardando…" : edit ? "Guardar cambios" : "Crear comisionista"}
          </button>
        </div>
      </div>
    </div>
  );
}
